import { ContextWindow } from "@asup/context-menu";
import { useState } from "react";
import { ArrowLeftRight, BoxArrowUpRight, DashCircle } from "react-bootstrap-icons";
import { getFromDataPath, isString } from "../../utility";
import { AnalysisSet, DataSubset, Group } from "../ars_ldm";
import { PATCH_ARS_STATE, REMOVE_WHERE_CLAUSE, useArsContext } from "../contexts";
import { ArsSelect } from "../inputs/ArsSelect";
import { isSubClauseWithReference } from "../typeGuards/isSubClauseWithReference";
import { AnalysisSetControl } from "./AnalysisSetControl";
import { DataSubsetControl } from "./DataSubsetControl";
import styles from "./WhereClauseConditionItemControl.module.css";

/**
 * Configuration for entity-specific settings in ReferencedEntityControl.
 */
export interface EntityConfiguration {
  /** Collection of entities from ArsContext state */
  entity?: AnalysisSet | DataSubset | Group;
  /** Name for the entity type */
  name: string;
  /** Label text for the dropdown */
  label: string;
  /** Options for the dropdown */
  options: { value: string; label: string }[];
}

export const adgEntity = {
  AnalysisSet: {
    name: "Analysis set",
    label: "Analysis set",
    arsKey: "analysisSets",
  },
  DataSubset: {
    name: "Data subset",
    label: "Data subset",
    arsKey: "dataSubsets",
  },
};

/**
 * Props for the ReferencedEntityControl component.
 */
export interface ReferencedEntityControlProps {
  /** RFC 6901 JSON Pointer path to the subClauseId field in ArsContext state */
  dataPath: string;
  /** Type of entity being referenced */
  entityType: keyof typeof adgEntity;
  /** Disabled state */
  disabled?: boolean;
}

/**
 * Generic control for selecting and linking to referenced entities (AnalysisSet or DataSubset).
 *
 * This component provides:
 * - A dropdown populated with available entities from ArsContext
 * - A link icon that opens the referenced entity in a ContextWindow for viewing/editing
 * - Renders the corresponding entity control (AnalysisSetControl or DataSubsetControl) based on entity type
 * - Consistent spacing to prevent layout shifts when the link icon appears/disappears
 *
 * The component reads the subClauseId from the specified dataPath and uses it to:
 * - Set the selected value in the dropdown
 * - Look up the full entity details when the link icon is clicked
 * - Display the entity control for viewing and editing
 *
 * @param props - Component props.
 * @param props.dataPath - RFC 6901 JSON Pointer path to the subClauseId field in ArsContext state.
 * @param props.entityType - Type of entity being referenced.
 * @param props.disabled - Optional flag to disable selection and navigation controls.
 * @returns A React functional component rendering the referenced entity control.
 */
export const ReferencedEntityControl = ({
  dataPath,
  entityType,
  disabled = false,
}: ReferencedEntityControlProps): React.ReactNode => {
  const { state, dispatch } = useArsContext();
  const [isContextWindowOpen, setIsContextWindowOpen] = useState(false);
  const subClausePath = dataPath.endsWith("/subClauseId")
    ? dataPath.substring(0, dataPath.lastIndexOf("/subClauseId"))
    : // istanbul ignore next
      "";
  const subClause = getFromDataPath(subClausePath, state, isSubClauseWithReference);
  const canSwapToCondition = subClause !== undefined;

  // Get the current subClauseId value
  const subClauseId = getFromDataPath(dataPath, state, isString) ?? "-- None --";

  /**
   * Get the entity configuration based on the entity type and subClauseId.
   * This includes the collection of entities, display name, label for the dropdown, and options for the dropdown.
   *
   * @param entityType - The type of entity being referenced (AnalysisSet or DataSubset)
   * @param subClauseId - The ID of the referenced entity to find the full details for
   * @return The entity configuration for the specified entity type, or undefined if the subClauseId does not correspond to a valid entity
   */
  const getEntityConfig = (): EntityConfiguration => {
    // Get the right entity collection based on the entity type
    const entities: Record<string, AnalysisSet | DataSubset> =
      entityType === "AnalysisSet" ? state.analysisSets : state.dataSubsets;
    // Find the specific entity being referenced using the subClauseId
    const entity: AnalysisSet | DataSubset | undefined = subClauseId
      ? entities[subClauseId]
      : undefined;

    // Return undefined if the subClauseId does not correspond to a valid entity
    const name = adgEntity[entityType].name;
    const label = adgEntity[entityType].label;
    const options = Object.values(entities).map((e) => ({ value: e.id, label: e.name }));
    if (subClauseId === "-- None --" || subClauseId === "")
      options.unshift({ value: "-- None --", label: "-- None --" });

    // Otherwise return the full entity configuration for the specified entity type
    return { entity, name, label, options };
  };
  const entityConfig = getEntityConfig();

  /**
   * Get the dataPath for the referenced entity based on entity type.
   *
   * @returns The RFC 6901 JSON Pointer path to the referenced entity in ArsContext state
   */
  const getEntityDataPath = (): string => {
    const typePrefix = adgEntity[entityType].arsKey;
    return `/${typePrefix}/${subClauseId}`;
  };

  /**
   * Handles opening the context window to view the referenced entity.
   */
  const handleOpenContextWindow = (): void => {
    setIsContextWindowOpen(true);
  };

  /**
   * Handles removing a where clause.
   * Reverts to single condition if only 2 clauses remain.
   */
  const handleRemoveClause = (): void => {
    const parentPath = dataPath.substring(
      0,
      dataPath.lastIndexOf("/compoundExpression/whereClauses/"),
    );
    dispatch({
      operation: REMOVE_WHERE_CLAUSE,
      dataPath: parentPath,
      id: subClauseId,
    });
  };

  /**
   * Handles swapping a referenced entity back to a condition clause.
   */
  const handleSwapToCondition = (): void => {
    /* istanbul ignore else */
    if (subClause && subClausePath) {
      dispatch({
        operation: PATCH_ARS_STATE,
        dataPath: subClausePath,
        value: {
          id: subClause.id,
          level: subClause.level,
          order: subClause.order,
          subClauseId: "",
          condition: {},
        },
      });
    }
  };

  return (
    <div className={styles.compactContainer}>
      <div className="flex-grow-1">
        <ArsSelect
          dataPath={dataPath}
          labelWidth={2}
          label={entityConfig.label}
          options={entityConfig.options}
          id={`${dataPath.replace(/\//g, "-")}-${entityType.toLowerCase()}`}
          disabled={disabled}
        />
      </div>
      {/* Preserve space for link icon even when not visible to prevent layout shift */}
      <div className={styles.actionButtons}>
        <ArrowLeftRight
          id={`${dataPath.replace(/\//g, "-")}-swap`}
          className={canSwapToCondition ? "text-primary" : "text-muted"}
          aria-label="Swap to condition"
          aria-disabled={!canSwapToCondition || disabled}
          onClick={canSwapToCondition && !disabled ? handleSwapToCondition : undefined}
          style={{ cursor: canSwapToCondition && !disabled ? "pointer" : "not-allowed" }}
        />
        {entityConfig.entity && (
          <>
            <BoxArrowUpRight
              className="text-primary"
              aria-label={`Open referenced ${entityType.toLowerCase()}`}
              aria-disabled={disabled}
              onClick={disabled ? undefined : handleOpenContextWindow}
              style={{ cursor: disabled ? "not-allowed" : "pointer" }}
            />
            <DashCircle
              id={`${dataPath.replace(/\//g, "-")}-remove`}
              className="text-danger"
              aria-label="Remove clause"
              aria-disabled={disabled}
              onClick={disabled ? undefined : () => handleRemoveClause()}
              style={{ cursor: disabled ? "not-allowed" : "pointer" }}
            />
          </>
        )}
      </div>
      {/* Display the entity details in the context window */}
      <ContextWindow
        id={`ref-${entityType.toLowerCase()}-${subClauseId}`}
        title={entityConfig.name}
        visible={isContextWindowOpen}
        onClose={() => setIsContextWindowOpen(false)}
        style={{
          width: "500px",
          minHeight: "300px",
          maxHeight: "70vh",
          maxWidth: "80vw",
        }}
      >
        {entityType === "DataSubset" ? (
          <DataSubsetControl
            dataPath={getEntityDataPath()}
            disabled={disabled}
          />
        ) : (
          <AnalysisSetControl
            dataPath={getEntityDataPath()}
            disabled={disabled}
          />
        )}
      </ContextWindow>
    </div>
  );
};

ReferencedEntityControl.displayName = "ReferencedEntityControl";
